import { useState, useRef, type KeyboardEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Button, ButtonGroup, Table, Row, Col } from 'react-bootstrap';
import { Search, Copy, Package, Barcode, Check, Loader2, AlertCircle } from 'lucide-react';
import type { OrderSummary, LineItem, WmsOrderSearchParams } from '../../../../infrastructure/OMSClient';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import {
  useStock,
  type ProductLookupResponse,
  type OutboundStockRequest,
  OutboundStatus,
} from '@/apps/hooks/stock/useStock';
import { toast } from 'react-hot-toast';
import { InboundStatus } from '../../../../models/InboundStatus';
import { getDeliveryCompanyName } from '../../orders/utils/deliveryCompany';
import { useAuth } from '../../../../providers/AuthProvider';
import { getStatusText, getStatusDescription } from '../../orders/utils/statusTextMapping';
import { WooCommerceOrderStatus } from '../../../../models/ProductTypes';
import { getStatusTextColor, getStatusBgColor } from '../../orders/utils/statusColor';
import { OrderLabelPrintButton } from '../../orders/components/OrderLabelPrintButton';
import { productAttributesClient } from '../../../../infrastructure/ProductAttributesClient';
import { isUkExclusiveShippingBlocked, UK_EXCLUSIVE_BLOCK_MESSAGE } from '../../orders/utils/ukExclusive';
import { processByProductId } from '@/pages/oms/orders/utils/processByProductId';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';
import { waybillOfParcelQr } from '@/utils/parcelQr';
import { toScanAscii } from '@/utils/scanAscii';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import {
  PRODUCT_BARCODE_ERROR,
  PRODUCT_BARCODE_HINT,
  isValidProductBarcode,
  normalizeProductBarcode,
} from '@/utils/productBarcode';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';

const SEARCH_TYPES = [
  { value: 'trackingNumber', label: 'Tracking Number' },
  { value: 'inboundTrackingOrderNumber', label: 'Inbound Order Number' },
] as const;

export type WmsSearchType = (typeof SEARCH_TYPES)[number]['value'];

/** A cancelled order is out of the inbound flow; inbounding its line books stock for a parcel that must not ship [SSD-7049]. */
export const isCancelledOrder = (order: Pick<OrderSummary, 'status'>): boolean =>
  order.status === WooCommerceOrderStatus.CANCELLED;

export const cancelledInboundMessage = (orderId: string): string =>
  `Order ${orderId} is cancelled — its lines cannot be marked as inbounded`;

export const buildWmsOrderSearchParams = (value: string, searchBy: WmsSearchType): WmsOrderSearchParams | null => {
  // A scan typed with the keyboard in Korean mode is read as the ASCII the scanner meant [SSD-7050].
  const trimmed = toScanAscii(value).trim();
  if (!trimmed) {
    return null;
  }

  if (searchBy === 'trackingNumber') {
    return { trackingNumber: waybillOfParcelQr(trimmed) };
  }

  return { inboundTrackingOrderNumber: trimmed };
};

export default function WMSViewOrdersPage() {
  const [searchBy, setSearchBy] = useState<WmsSearchType>('trackingNumber');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<OrderSummary[]>([]);
  const [productInfos, setProductInfos] = useState<Record<string, ProductLookupResponse>>({});
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [loadingInbound, setLoadingInbound] = useState<string | null>(null);
  const [loadingCancel, setLoadingCancel] = useState<string | null>(null);
  const [processingOutbound, setProcessingOutbound] = useState<Record<string, boolean>>({});
  const [barcodeInputs, setBarcodeInputs] = useState<Record<string, string>>({});
  const [savingBarcode, setSavingBarcode] = useState<Record<string, boolean>>({});
  const [savedBarcodes, setSavedBarcodes] = useState<Record<string, string>>({});
  const [temporaryLocationInputs, setTemporaryLocationInputs] = useState<Record<string, string>>({});
  const [savingTemporaryLocation, setSavingTemporaryLocation] = useState<Record<string, boolean>>({});
  const searchRef = useRef<HTMLInputElement>(null);
  const temporaryLocationRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const oms = useOMS();
  const stock = useStock();
  const { user } = useAuth();

  const loadProductInfos = async (results: OrderSummary[]) => {
    if (!results || results.length === 0) {
      setProductInfos({});
      return;
    }

    const uniqueSkus = [...new Set(results.flatMap((order) => order.lineItems).map((item) => item.sku))];
    const productInfoResults = await Promise.all(
      uniqueSkus.map(async (sku: string) => {
        try {
          const productInfo = await stock.getProductBySku(sku);
          return { sku, productInfo };
        } catch (error) {
          console.warn(`Failed to fetch product info for SKU ${sku}:`, error);
          return { sku, productInfo: null };
        }
      }),
    );

    const productInfoMap: Record<string, ProductLookupResponse> = {};
    productInfoResults.forEach(({ sku, productInfo }) => {
      if (productInfo) {
        productInfoMap[sku] = productInfo;
      }
    });
    setProductInfos(productInfoMap);
  };

  const handleSearch = async () => {
    const searchParams = buildWmsOrderSearchParams(searchValue, searchBy);

    if (!searchParams) {
      toast.error('Please enter a tracking or inbound order number');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setSearchError(null);

    try {
      const results = await oms.searchWmsOrders(searchParams);
      setSearchResults(results);
      await loadProductInfos(results);
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = `Failed to search orders: ${error instanceof Error ? error.message : 'Unknown error'}`;
      toast.error(errorMessage);
      setSearchError(errorMessage);
      setSearchResults([]);
      setProductInfos({});
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOrderClick = (orderId: string) => {
    window.open(`/oms/orders/${orderId}`, '_blank');
  };

  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      toast.success('Order ID copied to clipboard');
    } catch {
      toast.error('Failed to copy Order ID');
    }
  };

  const handleMarkAsInbounded = async (orderId: string, itemId: string) => {
    if (!itemId || !user) {
      toast.error('Item ID and user authentication are required');
      return;
    }

    if (!searchResults || searchResults.length === 0) {
      toast.error('No search results available');
      return;
    }

    // Find the specific order and line item
    const targetOrder = searchResults.find((order) => order.orderId === orderId);
    if (!targetOrder) {
      toast.error('Order not found');
      return;
    }

    const lineItem = targetOrder.lineItems.find((item: LineItem) => item.itemId === itemId);
    if (!lineItem) {
      toast.error('Line item not found');
      return;
    }

    if (isCancelledOrder(targetOrder)) {
      toast.error(cancelledInboundMessage(orderId));
      return;
    }

    setLoadingInbound(itemId);

    try {
      // The order may have been cancelled since the search, so its current status is read before any stock moves.
      const latestOrder = await oms.getOrderDetail(orderId);
      if (latestOrder && isCancelledOrder(latestOrder)) {
        setSearchResults((prevResults) =>
          prevResults.map((order) => (order.orderId === orderId ? latestOrder : order)),
        );
        toast.error(cancelledInboundMessage(orderId));
        return;
      }

      // Get productId with fallback logic
      let productId = parseInt(lineItem.productId, 10);

      if (isNaN(productId)) {
        // Fallback: get productId from SKU
        try {
          const productInfo = await stock.getProductBySku(lineItem.sku);
          productId = productInfo.productId;
        } catch (error) {
          throw new Error(
            `Unable to resolve productId from SKU ${lineItem.sku}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          );
        }
      }

      const currentTime = new Date().toISOString();

      const deleoInboundRequest: any = {
        productId: productId,
        qty: lineItem.quantity,
        orderId: orderId,
        itemId: lineItem.itemId,
        auditor: user.name || user.email || 'unknown-user',
        effectiveAt: currentTime,
        locationId: 'WH#00',
      };

      const trackingNumber = lineItem.sourcingShipmentTrack?.trackingNumber || lineItem.cpOrderNumber;
      if (trackingNumber) {
        deleoInboundRequest.trackingNumber = trackingNumber;
      }

      if (lineItem.deliveryCompanyCd) {
        deleoInboundRequest.carrier = getDeliveryCompanyName(lineItem.deliveryCompanyCd);
      }

      await stock.deleoInbound(deleoInboundRequest);

      // Then update OMS inbound status
      const updatedOrder = await oms.updateLineItemInboundStatus(
        orderId,
        itemId,
        InboundStatus.INBOUNDED,
        user?.name || user?.email || 'unknown-user',
      );

      if (updatedOrder) {
        // Update the specific order in the results array
        setSearchResults((prevResults) =>
          prevResults.map((order) => (order.orderId === orderId ? updatedOrder : order)),
        );
        // The parcel now goes to the rebin station, so the cursor waits in its Temporary Location with the
        // value selected and the slot scanned there overwrites it [SSD-7052].
        const temporaryLocationInput = temporaryLocationRefs.current[orderId];
        temporaryLocationInput?.focus();
        temporaryLocationInput?.select();
      } else {
        toast.error('Deleo inbound succeeded but failed to update OMS status');
      }
    } catch (error) {
      console.error('Error processing inbound:', error);
      toast.error(`Failed to process inbound: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingInbound(null);
    }
  };

  const handleCancelInbound = async (orderId: string, itemId: string) => {
    if (!itemId || !user) {
      toast.error('Item ID and user authentication are required');
      return;
    }

    if (!searchResults || searchResults.length === 0) {
      toast.error('No search results available');
      return;
    }

    // Find the specific order and line item
    const targetOrder = searchResults.find((order) => order.orderId === orderId);
    if (!targetOrder) {
      toast.error('Order not found');
      return;
    }

    const lineItem = targetOrder.lineItems.find((item: LineItem) => item.itemId === itemId);
    if (!lineItem) {
      toast.error('Line item not found');
      return;
    }

    setLoadingCancel(itemId);

    try {
      // Get productId with fallback logic
      let productId = parseInt(lineItem.productId, 10);

      if (isNaN(productId)) {
        // Fallback: get productId from SKU
        try {
          const productInfo = await stock.getProductBySku(lineItem.sku);
          productId = productInfo.productId;
        } catch (error) {
          throw new Error(
            `Unable to resolve productId from SKU ${lineItem.sku}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          );
        }
      }

      const outboundRequest: OutboundStockRequest = {
        productId: productId,
        qty: lineItem.quantity,
        orderId: orderId,
        itemId,
        auditor: user.name || user.email || 'unknown-user',
        locationId: 'WH#00',
        status: OutboundStatus.INBOUND_CANCEL,
      };

      const trackingNumber = lineItem.sourcingShipmentTrack?.trackingNumber || lineItem.cpOrderNumber;
      if (trackingNumber) {
        outboundRequest.trackingNumber = trackingNumber;
      }

      if (lineItem.deliveryCompanyCd) {
        outboundRequest.carrier = getDeliveryCompanyName(lineItem.deliveryCompanyCd);
      }

      const cancellation = await stock.outboundStock(outboundRequest);
      if (!cancellation.success) {
        throw new Error('Failed to cancel inbound stock');
      }

      // Then update OMS inbound status back to PENDING
      const updatedOrder = await oms.updateLineItemInboundStatus(
        orderId,
        itemId,
        InboundStatus.PENDING,
        user?.name || user?.email || 'unknown-user',
      );

      if (updatedOrder) {
        // Update the specific order in the results array
        setSearchResults((prevResults) =>
          prevResults.map((order) => (order.orderId === orderId ? updatedOrder : order)),
        );
      } else {
        toast.error('Inbound cancel succeeded but failed to update OMS status');
      }
    } catch (error) {
      console.error('Error cancelling inbound:', error);
      toast.error(`Failed to cancel inbound: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingCancel(null);
    }
  };

  const getInboundStatusBadgeClass = (status?: InboundStatus) => {
    switch (status) {
      case InboundStatus.INBOUNDED:
        return 'bg-success';
      case InboundStatus.PENDING:
      case undefined:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  };

  const getInboundStatusText = (status?: InboundStatus) => {
    return status || InboundStatus.PENDING;
  };

  const canOutboundToDeleo = (order: OrderSummary) => {
    return (
      order.status === WooCommerceOrderStatus.PROCESSING &&
      order.lineItems.length > 0 &&
      order.lineItems.every((item) => item.inboundStatus === InboundStatus.INBOUNDED)
    );
  };

  const handleBarcodeChange = (sku: string, value: string) => {
    setBarcodeInputs((prev) => ({ ...prev, [sku]: normalizeProductBarcode(value) }));
  };

  const handleSaveBarcode = async (sku: string, productId: number) => {
    const barcode = barcodeInputs[sku];
    if (!barcode || !isValidProductBarcode(barcode)) {
      toast.error(PRODUCT_BARCODE_ERROR);
      return;
    }

    setSavingBarcode((prev) => ({ ...prev, [sku]: true }));

    try {
      const result = await productAttributesClient.updateBarcode(productId, barcode);
      if (result.success) {
        toast.success(result.message);
        setSavedBarcodes((prev) => ({ ...prev, [sku]: barcode }));
        const prevInfo = productInfos[sku];
        if (prevInfo) {
          setProductInfos((prev) => ({
            ...prev,
            [sku]: { ...prevInfo, barcode },
          }));
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(`Failed to save barcode: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingBarcode((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleBarcodeKeyDown = (e: KeyboardEvent<HTMLInputElement>, sku: string, productId: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveBarcode(sku, productId);
    }
  };

  const handleTemporaryLocationChange = (orderId: string, value: string) => {
    setTemporaryLocationInputs((prev) => ({ ...prev, [orderId]: value }));
  };

  const handleSaveTemporaryLocation = async (orderId: string) => {
    if (!user) {
      toast.error('User authentication is required');
      return;
    }
    const temporaryLocation = (temporaryLocationInputs[orderId] ?? '').trim();
    if (!temporaryLocation) {
      toast.error('Temporary location is required');
      return;
    }

    setSavingTemporaryLocation((prev) => ({ ...prev, [orderId]: true }));
    try {
      const updatedOrder = await oms.updateOrder(orderId, {
        temporaryLocation,
        auditEntry: { operator: user.name || user.email || 'unknown-user', source: 'ADMIN_WMS' },
      });
      if (updatedOrder) {
        setSearchResults((prev) => prev.map((order) => (order.orderId === orderId ? updatedOrder : order)));
        setTemporaryLocationInputs((prev) => {
          const next = { ...prev };
          delete next[orderId];
          return next;
        });
        toast.success('Temporary location saved');
      } else {
        toast.error('Failed to save temporary location');
      }
    } catch (error) {
      toast.error(`Failed to save temporary location: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingTemporaryLocation((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleTemporaryLocationKeyDown = (e: KeyboardEvent<HTMLInputElement>, orderId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTemporaryLocation(orderId);
    }
  };

  const hasBarcode = (sku: string, itemBarcode?: string): boolean => {
    return Boolean(itemBarcode || productInfos[sku]?.barcode || savedBarcodes[sku]);
  };

  const getDisplayBarcode = (sku: string, itemBarcode?: string): string => {
    return savedBarcodes[sku] || itemBarcode || productInfos[sku]?.barcode || '';
  };

  const handleOutboundToDeleo = async (order: OrderSummary) => {
    if (!user) {
      toast.error('User authentication required');
      return;
    }

    if (order.status !== WooCommerceOrderStatus.PROCESSING) {
      toast.error('Can only outbound to Deleo for orders with "processing" status');
      return;
    }

    if (isUkExclusiveShippingBlocked(order)) {
      toast.error(UK_EXCLUSIVE_BLOCK_MESSAGE);
      return;
    }

    if (!canOutboundToDeleo(order)) {
      toast.error('All line items must be INBOUNDED to proceed with outbound');
      return;
    }

    setProcessingOutbound((prev) => ({ ...prev, [order.orderId]: true }));

    try {
      const results = await processByProductId(
        order.lineItems,
        async (item) => (await stock.getProductBySku(item.sku)).productId,
        async (item, productId) => {
          const outboundRequest: OutboundStockRequest = {
            productId,
            qty: item.quantity,
            locationId: 'WH#00',
            status: OutboundStatus.CONFIRMED,
            orderId: order.orderId,
            auditor: user.name || user.email || 'unknown-user',
          };

          if (item.itemId) {
            outboundRequest.itemId = item.itemId;
          }

          const trackingNumber = item.sourcingShipmentTrack?.trackingNumber || item.cpOrderNumber;
          if (trackingNumber) {
            outboundRequest.trackingNumber = trackingNumber;
          }
          if (item.deliveryCompanyCd) {
            outboundRequest.carrier = item.deliveryCompanyCd;
          }

          return stock.outboundStock(outboundRequest);
        },
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Failed to outbound ${order.lineItems[index]?.sku ?? 'unknown SKU'}:`, result.reason);
        }
      });
      const failedItems = results.filter((result) => result.status === 'rejected' || !result.value.success);

      if (failedItems.length === 0) {
        toast.success(`Successfully outbounded ${order.lineItems.length} items to Deleo BaroShip`);

        try {
          await oms.updateOrderStatus(
            order.orderId,
            WooCommerceOrderStatus.PREPARE_SHIPMENT,
            user.name || user.email || 'unknown-user',
          );
          toast.success('Order status updated to PREPARE_FOR_SHIPMENT');
        } catch (statusError) {
          console.error('Error updating order status to PREPARE_FOR_SHIPMENT:', statusError);
          toast.error('Outbound successful but failed to update order status to PREPARE_FOR_SHIPMENT');
        }

        await handleSearch();
      } else {
        toast.error(`${failedItems.length} items failed to outbound. Check console for details.`);
      }
    } catch (error) {
      console.error('Error processing outbound:', error);
      toast.error(`Failed to outbound items: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingOutbound((prev) => ({ ...prev, [order.orderId]: false }));
    }
  };

  return (
    <div className="container mx-auto py-4 space-y-4" style={{ maxWidth: 'calc(66.666vw + 440px)' }}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">WMS - View Orders</h1>
      </div>

      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle>Search Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <Row className="align-items-center g-2">
            <Col xs="auto" style={{ minWidth: '320px' }}>
              <ButtonGroup aria-label="WMS search value type" className="w-100">
                {SEARCH_TYPES.map((type) => (
                  <Button
                    key={type.value}
                    variant={searchBy === type.value ? 'dark' : 'outline-dark'}
                    aria-pressed={searchBy === type.value}
                    onClick={() => setSearchBy(type.value)}
                  >
                    {type.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>

            <Col>
              <CameraScanField label="Scan search value with camera" disabled={loading}>
                <Input
                  ref={searchRef}
                  placeholder={`Enter ${SEARCH_TYPES.find((type) => type.value === searchBy)?.label.toLowerCase()}`}
                  value={searchValue}
                  {...asciiScanField(setSearchValue, handleKeyPress)}
                  disabled={loading}
                  className="w-100"
                />
              </CameraScanField>
            </Col>

            <Col xs="auto">
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={loading || !searchValue.trim()}
                className="d-flex align-items-center"
                style={{ gap: '8px' }}
              >
                <Search style={{ width: '16px', height: '16px' }} />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Col>
          </Row>
        </CardContent>
      </Card>

      {/* Results Section */}
      {hasSearched && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Searching...</p>
              </div>
            ) : searchError ? (
              <div className="text-center py-8">
                <div className="d-flex flex-column align-items-center text-danger" role="alert">
                  <AlertCircle style={{ width: '36px', height: '36px', marginBottom: '8px' }} />
                  <p className="mb-1 fw-semibold">Search failed</p>
                  <small>{searchError}</small>
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div>
                {searchResults.map((order) => (
                  <div key={order.orderId} className="mb-4">
                    {/* Order Summary */}
                    <div className="mb-4 p-3 bg-light rounded">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-2">
                            <strong>Deleo Tracking No:</strong>
                            <span className="ms-2 text-primary">{order.deleoData?.deleoTrackingNo || '-'}</span>
                          </div>
                          {order.deleoData?.deleoStatus && (
                            <div className="mb-2">
                              <strong>Deleo Status:</strong>
                              <span
                                className="ms-2 badge"
                                style={{
                                  color: getStatusTextColor(order.deleoData.deleoStatus.name || ''),
                                  backgroundColor: getStatusBgColor(order.deleoData.deleoStatus.name || ''),
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {getStatusText(order.deleoData.deleoStatus.name || '')} (
                                {getStatusDescription(order.deleoData.deleoStatus.name || '')})
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="col-md-8">
                          <div className="mb-2 d-flex align-items-center" style={{ gap: '0.5rem', flexWrap: 'nowrap' }}>
                            <strong style={{ whiteSpace: 'nowrap' }}>Order ID:</strong>
                            <span
                              className="text-info"
                              style={{ cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                              onClick={() => handleOrderClick(order.orderId)}
                            >
                              {order.orderId}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopyOrderId(order.orderId)}
                              style={{ padding: '2px 6px', flexShrink: 0 }}
                            >
                              <Copy style={{ width: '12px', height: '12px' }} />
                            </Button>
                            <strong style={{ whiteSpace: 'nowrap' }}>Order Status:</strong>
                            <span
                              className="badge"
                              style={{
                                color: getStatusTextColor(order.status),
                                backgroundColor: getStatusBgColor(order.status),
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.875rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {order.status &&
                                order.status
                                  .split('-')
                                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ')}
                            </span>
                            <OrderLabelPrintButton order={order} />
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleOutboundToDeleo(order)}
                              disabled={!canOutboundToDeleo(order) || processingOutbound[order.orderId]}
                              title={
                                !canOutboundToDeleo(order)
                                  ? 'All line items must be INBOUNDED to proceed with outbound'
                                  : ''
                              }
                              style={{ flexShrink: 0 }}
                            >
                              <Package style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                              {processingOutbound[order.orderId] ? 'Processing...' : 'Outbound to Deleo BaroShip'}
                            </Button>
                          </div>
                          <div className="mb-2 d-flex align-items-center" style={{ gap: '0.5rem' }}>
                            <strong style={{ whiteSpace: 'nowrap' }}>Total Quantity:</strong>
                            <span className="text-dark fw-bold" style={{ whiteSpace: 'nowrap' }}>
                              {order.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                            </span>
                            <strong style={{ whiteSpace: 'nowrap' }}>Country:</strong>
                            <span className="text-dark fw-bold" style={{ whiteSpace: 'nowrap' }}>
                              {order.shippingAddress?.country || '-'}
                            </span>
                          </div>
                          <div className="mb-2 d-flex align-items-center" style={{ gap: '0.5rem' }}>
                            <strong style={{ whiteSpace: 'nowrap' }}>Temporary Location:</strong>
                            <CameraScanField
                              label="Scan temporary location with camera"
                              disabled={savingTemporaryLocation[order.orderId]}
                            >
                              <Input
                                ref={(element) => {
                                  temporaryLocationRefs.current[order.orderId] = element;
                                }}
                                value={temporaryLocationInputs[order.orderId] ?? order.temporaryLocation ?? ''}
                                {...asciiScanField(
                                  (value) => handleTemporaryLocationChange(order.orderId, value),
                                  (e) => handleTemporaryLocationKeyDown(e, order.orderId),
                                )}
                                disabled={savingTemporaryLocation[order.orderId]}
                                placeholder="e.g. A1"
                                style={{ maxWidth: '120px' }}
                              />
                            </CameraScanField>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleSaveTemporaryLocation(order.orderId)}
                              disabled={savingTemporaryLocation[order.orderId]}
                              style={{ flexShrink: 0 }}
                            >
                              {savingTemporaryLocation[order.orderId] ? (
                                <Loader2
                                  style={{ width: '12px', height: '12px', animation: 'spin 1s linear infinite' }}
                                />
                              ) : (
                                <Check style={{ width: '12px', height: '12px' }} />
                              )}
                            </Button>
                          </div>
                          {isCancelledOrder(order) && (
                            <div className="mb-2 text-danger fw-semibold" role="alert">
                              Cancelled order — do not pack. Its lines cannot be marked as inbounded.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Order Items Table */}
                    {order.lineItems && order.lineItems.length > 0 ? (
                      <div>
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead className="table-dark">
                              <tr>
                                <th>SKU</th>
                                <th>Image</th>
                                <th>Product Name</th>
                                <th>Product Name KR</th>
                                <th>Size</th>
                                <th>Quantity</th>
                                <th>Barcode</th>
                                <th>Product Link</th>
                                <th>Inbound Order No</th>
                                <th>Tracking No</th>
                                <th>Delivery Company</th>
                                <th>Inbound Status</th>
                                <th>Sourcing Route</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.lineItems.map((item: LineItem, index: number) => (
                                <tr key={index}>
                                  <td className="font-monospace">{item.sku}</td>
                                  <td>
                                    {item.wooCommerceImageUrl ? (
                                      <img
                                        src={item.wooCommerceImageUrl}
                                        alt={item.name}
                                        className="rounded border"
                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <span className="text-muted">No image</span>
                                    )}
                                  </td>
                                  <td>{item.name}</td>
                                  <td>{item.nameKor || '-'}</td>
                                  <td>{productInfos[item.sku]?.size || '-'}</td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary">{item.quantity}</span>
                                  </td>
                                  <td style={{ minWidth: '180px' }}>
                                    {hasBarcode(item.sku, item.barcode) ? (
                                      <div className="d-flex align-items-center gap-1">
                                        <Check style={{ width: '14px', height: '14px', color: 'green' }} />
                                        <span className="font-monospace text-success">
                                          {getDisplayBarcode(item.sku, item.barcode)}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="d-flex align-items-center gap-1">
                                        <Barcode style={{ width: '16px', height: '16px', color: '#6c757d' }} />
                                        <Input
                                          placeholder={PRODUCT_BARCODE_HINT}
                                          value={barcodeInputs[item.sku] || ''}
                                          aria-invalid={
                                            (barcodeInputs[item.sku] ?? '') !== '' &&
                                            !isValidProductBarcode(barcodeInputs[item.sku] ?? '')
                                              ? true
                                              : undefined
                                          }
                                          aria-describedby={
                                            (barcodeInputs[item.sku] ?? '') !== '' &&
                                            !isValidProductBarcode(barcodeInputs[item.sku] ?? '')
                                              ? `barcode-error-${order.orderId}-${item.itemId ?? index}`
                                              : undefined
                                          }
                                          {...asciiScanField(
                                            (value) => handleBarcodeChange(item.sku, value),
                                            (e) =>
                                              handleBarcodeKeyDown(e, item.sku, productInfos[item.sku]?.productId || 0),
                                          )}
                                          disabled={savingBarcode[item.sku]}
                                          style={{ width: '110px', fontSize: '12px', padding: '2px 6px' }}
                                        />
                                        <Button
                                          variant={
                                            isValidProductBarcode(barcodeInputs[item.sku] || '')
                                              ? 'success'
                                              : 'outline-secondary'
                                          }
                                          size="sm"
                                          onClick={() =>
                                            handleSaveBarcode(item.sku, productInfos[item.sku]?.productId || 0)
                                          }
                                          disabled={
                                            savingBarcode[item.sku] ||
                                            !isValidProductBarcode(barcodeInputs[item.sku] || '') ||
                                            !productInfos[item.sku]?.productId
                                          }
                                          style={{ padding: '2px 6px' }}
                                        >
                                          {savingBarcode[item.sku] ? (
                                            <Loader2
                                              style={{
                                                width: '12px',
                                                height: '12px',
                                                animation: 'spin 1s linear infinite',
                                              }}
                                            />
                                          ) : (
                                            <Check style={{ width: '12px', height: '12px' }} />
                                          )}
                                        </Button>
                                        {(barcodeInputs[item.sku] ?? '') !== '' &&
                                          !isValidProductBarcode(barcodeInputs[item.sku] ?? '') && (
                                            <span
                                              id={`barcode-error-${order.orderId}-${item.itemId ?? index}`}
                                              className="text-danger"
                                              role="alert"
                                            >
                                              {PRODUCT_BARCODE_ERROR}
                                            </span>
                                          )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {(() => {
                                      const productLink = productInfos[item.sku]?.productLink;
                                      return productLink ? (
                                        <a
                                          href={productLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary text-decoration-none"
                                          style={{ fontSize: '12px' }}
                                        >
                                          View Product
                                        </a>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: '12px' }}>
                                          -
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="font-monospace">{item.sourcingShipmentTrack?.orderNumber || '-'}</td>
                                  <td className="font-monospace text-primary">
                                    {(() => {
                                      const cpTracking = item.cpOrderNumber;
                                      const sourcingTracking = item.sourcingShipmentTrack?.trackingNumber;
                                      const trackings = [cpTracking, sourcingTracking].filter(Boolean);
                                      return trackings.length > 0 ? trackings.join(' / ') : '-';
                                    })()}
                                  </td>
                                  <td>
                                    <span className="badge bg-info text-dark">
                                      {item.deliveryCompanyCd ? getDeliveryCompanyName(item.deliveryCompanyCd) : '-'}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className={`badge ${getInboundStatusBadgeClass(item.inboundStatus)}`}>
                                      {getInboundStatusText(item.inboundStatus)}
                                    </span>
                                  </td>
                                  <td>{item.sourcingRoute ? fulfillmentSourceLabel(item.sourcingRoute) : '-'}</td>
                                  <td className="text-center">
                                    {item.inboundStatus !== InboundStatus.INBOUNDED && (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleMarkAsInbounded(order.orderId, item.itemId || '')}
                                        disabled={
                                          !item.itemId || loadingInbound === item.itemId || isCancelledOrder(order)
                                        }
                                        title={isCancelledOrder(order) ? cancelledInboundMessage(order.orderId) : ''}
                                        className="d-flex align-items-center"
                                        style={{ gap: '4px', fontSize: '12px' }}
                                      >
                                        {loadingInbound === item.itemId ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm"
                                              role="status"
                                              aria-hidden="true"
                                            ></span>
                                            <span>Updating...</span>
                                          </>
                                        ) : isCancelledOrder(order) ? (
                                          <span>Order Cancelled</span>
                                        ) : (
                                          <>
                                            <span>Mark as Inbounded</span>
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    {item.inboundStatus === InboundStatus.INBOUNDED && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleCancelInbound(order.orderId, item.itemId || '')}
                                        disabled={!item.itemId || loadingCancel === item.itemId}
                                        className="d-flex align-items-center"
                                        style={{ gap: '4px', fontSize: '12px' }}
                                      >
                                        {loadingCancel === item.itemId ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm"
                                              role="status"
                                              aria-hidden="true"
                                            ></span>
                                            <span>Cancelling...</span>
                                          </>
                                        ) : (
                                          <>
                                            <span>Cancel Inbound</span>
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <div className="mt-3 text-muted">
                          <small>Found {order.lineItems.length} item(s) in this order</small>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-muted">
                          <p>Order found but no items available</p>
                          <small>This order may not have any line items</small>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div className="mt-3 text-muted">
                  <small>Found {searchResults.length} order(s)</small>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-muted">
                  <p>No orders found</p>
                  <small>Try searching with a different value</small>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
